import {
    KNOWN_REALTIME_JOB_TYPES,
    type JobProgressMessage,
    type KnownRealtimeJobType,
} from './models';

export function normalizeJobProgressMessage(
    value: unknown
): JobProgressMessage | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const jobType = readValue<JobProgressMessage['jobType']>(
        candidate,
        'jobType',
        'JobType'
    );
    const normalizedCandidate: Partial<JobProgressMessage> = {
        jobId: readString(candidate, 'jobId', 'JobId'),
        jobType,
        status: readValue(candidate, 'status', 'Status'),
        message: readNullableString(candidate, 'message', 'Message'),
        percentComplete: readNullableNumber(
            candidate,
            'percentComplete',
            'PercentComplete'
        ),
        occurredAtUtc: readString(candidate, 'occurredAtUtc', 'OccurredAtUtc'),
        correlationId: readNullableString(
            candidate,
            'correlationId',
            'CorrelationId'
        ),
        documentPublicId: readNullableString(
            candidate,
            'documentPublicId',
            'DocumentPublicId'
        ),
        buildingId: readNullableString(candidate, 'buildingId', 'BuildingId'),
        packId: normalizePackId(candidate, jobType),
        failureCode: readNullableString(
            candidate,
            'failureCode',
            'FailureCode'
        ),
        actualBytes: readNullableNumber(
            candidate,
            'actualBytes',
            'ActualBytes'
        ),
        allowedBytes: readNullableNumber(
            candidate,
            'allowedBytes',
            'AllowedBytes'
        ),
        completedItems: readNullableNumber(
            candidate,
            'completedItems',
            'CompletedItems'
        ),
        totalItems: readNullableNumber(candidate, 'totalItems', 'TotalItems'),
    };

    return isJobProgressMessage(normalizedCandidate)
        ? normalizedCandidate
        : null;
}

function isJobProgressMessage(value: unknown): value is JobProgressMessage {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<JobProgressMessage>;

    return (
        typeof candidate.jobId === 'string' &&
        isRealtimeJobType(candidate.jobType) &&
        isRealtimeJobStatus(candidate.status) &&
        typeof candidate.occurredAtUtc === 'string' &&
        (typeof candidate.message === 'string' || candidate.message === null) &&
        (typeof candidate.percentComplete === 'number' ||
            candidate.percentComplete === null) &&
        (typeof candidate.correlationId === 'string' ||
            candidate.correlationId === null) &&
        (typeof candidate.documentPublicId === 'string' ||
            candidate.documentPublicId === null) &&
        (typeof candidate.buildingId === 'string' ||
            candidate.buildingId === null) &&
        (typeof candidate.packId === 'string' || candidate.packId === null) &&
        (typeof candidate.failureCode === 'string' ||
            candidate.failureCode === null) &&
        (typeof candidate.actualBytes === 'number' ||
            candidate.actualBytes === null) &&
        (typeof candidate.allowedBytes === 'number' ||
            candidate.allowedBytes === null) &&
        (typeof candidate.completedItems === 'number' ||
            candidate.completedItems === null) &&
        (typeof candidate.totalItems === 'number' ||
            candidate.totalItems === null)
    );
}

function normalizePackId(
    candidate: Record<string, unknown>,
    jobType: unknown
): string | null {
    if (jobType !== 'DocumentPackExport') {
        return null;
    }

    const packId = readValue<unknown>(candidate, 'packId', 'PackId');

    if (typeof packId !== 'string') {
        return null;
    }

    const normalizedPackId = packId.trim();

    return normalizedPackId.length > 0 ? normalizedPackId : null;
}

function isRealtimeJobType(
    value: unknown
): value is JobProgressMessage['jobType'] {
    return typeof value === 'string' && value.trim().length > 0;
}

export function isKnownRealtimeJobType(
    value: string
): value is KnownRealtimeJobType {
    return KNOWN_REALTIME_JOB_TYPES.some((jobType) => jobType === value);
}

function isRealtimeJobStatus(
    value: unknown
): value is JobProgressMessage['status'] {
    return (
        value === 'Queued' ||
        value === 'Started' ||
        value === 'Progress' ||
        value === 'Completed' ||
        value === 'Failed' ||
        value === 'Skipped' ||
        value === 'Expired'
    );
}

function readValue<T>(
    candidate: Record<string, unknown>,
    camelKey: string,
    pascalKey: string
): T | undefined {
    return (candidate[camelKey] ?? candidate[pascalKey]) as T | undefined;
}

function readString(
    candidate: Record<string, unknown>,
    camelKey: string,
    pascalKey: string
): string | undefined {
    return readValue<string>(candidate, camelKey, pascalKey);
}

function readNullableString(
    candidate: Record<string, unknown>,
    camelKey: string,
    pascalKey: string
): string | null {
    const value = readValue<unknown>(candidate, camelKey, pascalKey);

    return typeof value === 'string' || value === null ? value : null;
}

function readNullableNumber(
    candidate: Record<string, unknown>,
    camelKey: string,
    pascalKey: string
): number | null {
    const value = readValue<unknown>(candidate, camelKey, pascalKey);

    return typeof value === 'number' || value === null ? value : null;
}
